import React from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { trMoney } from '../../utils/accountingFormat';
import EmptyStateCard from '../expense/EmptyStateCard';

function buildSubtitle(row) {
  if (row.paymentType === 'SESSION') {
    const parts = ['Seans borcu', row.activityName || 'Seans', row.dayISO || 'Tarih yok'];
    if (row.trainerName) parts.push(row.trainerName);
    return parts.join(' • ');
  }

  if (row.paymentType === 'PACKAGE') {
    const parts = ['Paket borcu', row.packageName || null];
    if (row.trainerName) parts.push(row.trainerName);
    return parts.filter(Boolean).join(' • ');
  }

  return ['Bekleyen odeme', row.trainerName || null].filter(Boolean).join(' • ');
}

function buildTypeChip(row) {
  if (row.paymentType === 'SESSION') {
    return { label: 'Seans', color: 'info' };
  }
  if (row.paymentType === 'PACKAGE') {
    return { label: 'Paket', color: 'secondary' };
  }
  return { label: row.paymentType || 'Odeme', color: 'default' };
}

function buildRowKey(row, index) {
  if (row.paymentType === 'SESSION')
    return `session-${row.sessionParticipantId || row.sessionId || index}`;
  if (row.paymentType === 'PACKAGE') return `package-${row.memberId || index}`;
  return `payment-${row.paymentId || row.memberId || index}`;
}

export default function IncomeReceivablesCard({
  rows = [],
  onReceivePayment,
  onSendReminder,
  onOpenMember,
}) {
  return (
    <Card
      sx={{
        borderRadius: '18px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      <MDBox sx={{ px: 1.25, py: 1.1 }}>
        <MDTypography variant="button" fontWeight="bold">
          Bekleyen Tahsilatlar
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
          Seans ve paket borclari
        </MDTypography>
      </MDBox>

      <Divider />

      <MDBox sx={{ p: 1.25, display: 'grid', gap: 0.8 }}>
        {!rows.length ? (
          <EmptyStateCard
            icon="account_balance_wallet"
            title="Tahsilat kaydi yok"
            desc="Secilen filtreye uygun bekleyen tahsilat bulunamadi."
          />
        ) : (
          rows.map((row, index) => {
            const typeChip = buildTypeChip(row);
            const amount = Number(row.amountTry || 0);

            return (
              <MDBox
                key={buildRowKey(row, index)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  p: 1,
                  borderRadius: '14px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  flexWrap: 'wrap',
                }}
              >
                <MDBox sx={{ minWidth: 0, flex: 1 }}>
                  <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      onClick={() => onOpenMember?.(row)}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                      }}
                    >
                      {row.fullName || row.payerName || 'Isimsiz kayit'}
                    </MDTypography>

                    <Chip
                      size="small"
                      color={typeChip.color}
                      label={typeChip.label}
                      sx={{ fontWeight: 800, height: 24 }}
                    />
                  </MDBox>

                  <MDTypography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
                  >
                    {row.phone || row.payerPhone || 'Telefon yok'}
                  </MDTypography>

                  <MDTypography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    {buildSubtitle(row)}
                  </MDTypography>
                </MDBox>

                <MDBox
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.8,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Chip
                    size="small"
                    color={amount > 0 ? 'warning' : 'default'}
                    label={trMoney(amount)}
                    sx={{ fontWeight: 900 }}
                  />

                  <Button
                    size="small"
                    startIcon={<Icon fontSize="small">campaign</Icon>}
                    onClick={() => onSendReminder?.(row)}
                    disabled={!row.phone && !row.payerPhone}
                  >
                    Sms
                  </Button>

                  <Button
                    size="small"
                    startIcon={<Icon fontSize="small">payments</Icon>}
                    onClick={() => onReceivePayment?.(row)}
                  >
                    Odeme al
                  </Button>
                </MDBox>
              </MDBox>
            );
          })
        )}
      </MDBox>
    </Card>
  );
}
